import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, ArrowUpRight, Plus, ChevronRight, Loader2 } from "lucide-react";

import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import DossierForm from "@/components/dossier/DossierForm";

import { KpiCard } from "@/components/wyvio-ui/signature/KpiCard";
import { DossierCard } from "@/components/wyvio-ui/signature/DossierCard";
import { DeliveryCard } from "@/components/wyvio-ui/signature/DeliveryCard";
import { HeroUrgent } from "@/components/wyvio-ui/signature/HeroUrgent";
import { BtnSignature } from "@/components/wyvio-ui/signature/BtnSignature";
import { StatusChip } from "@/components/wyvio-ui/signature/StatusChip";

/**
 * DashboardV2 — le vrai Dashboard WYVIO branché sur l'API existante.
 * Refonte visuelle uniquement, endpoints inchangés.
 *
 * Route : /app/dashboard  (l'ancien /app/dashboard-classic reste dispo pour rollback)
 */

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("fr-FR", {
      weekday: "long",
      day: "numeric",
      month: "long",
    });
  } catch {
    return "";
  }
};

const guessSilhouette = (vehicleName) => {
  const v = (vehicleName || "").toLowerCase();
  if (/gt3|coupe|coupé|911|m4|corvette|amg|f-type/.test(v)) return "coupe";
  if (/suv|classe g|range rover|q7|q8|x5|x7|cayenne|glc|glk|glb/.test(v)) return "suv";
  if (/rs6|break|estate|touring|allroad|shooting/.test(v)) return "break";
  return "berline";
};

// Phrase intelligente auto-générée depuis les totaux
const buildIntelligentSentence = (t) => {
  const bits = [];
  if (t.todays_deliveries > 0) {
    bits.push(
      <span key="d" className="text-[#151515]">
        {t.todays_deliveries} livraison{t.todays_deliveries > 1 ? "s" : ""} programmée{t.todays_deliveries > 1 ? "s" : ""} aujourd&apos;hui.
      </span>,
    );
  }
  if (t.blocked > 0) {
    bits.push(
      <span key="b"> {t.blocked} dossier{t.blocked > 1 ? "s" : ""} bloqué{t.blocked > 1 ? "s" : ""}.</span>,
    );
  } else {
    bits.push(<span key="ok" className="text-[#151515]"> Aucun blocage critique.</span>);
  }
  if (t.active_dossiers > 0) {
    bits.push(<span key="a"> {t.active_dossiers} dossier{t.active_dossiers > 1 ? "s" : ""} actif{t.active_dossiers > 1 ? "s" : ""}.</span>);
  }
  return bits;
};

export default function DashboardV2() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    api
      .getDashboardSummary(null)
      .then((s) => {
        if (!cancel) {
          setSummary(s);
          setError(null);
        }
      })
      .catch((e) => !cancel && setError(e.message || "Erreur de chargement"))
      .finally(() => !cancel && setLoading(false));
    return () => {
      cancel = true;
    };
  }, []);

  const firstName = (user?.name || "").split(" ")[0] || "Stéphane";

  if (loading && !summary) {
    return (
      <div
        className="wyvio-signature-bg font-neue-haas -mx-6 lg:-mx-10 -my-8 lg:-my-12 min-h-screen"
        data-testid="page-dashboard-v2"
      >
        <div className="flex items-center justify-center py-40 text-[#8A8A80]">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          <span className="text-[13px] font-medium">Chargement du centre de pilotage…</span>
        </div>
      </div>
    );
  }

  if (error && !summary) {
    return (
      <div className="wyvio-signature-bg font-neue-haas -mx-6 lg:-mx-10 -my-8 lg:-my-12 min-h-screen p-16">
        <p className="text-[13px] font-medium text-[#B7292E]">{error}</p>
      </div>
    );
  }

  const t = summary.totals;
  const priorityActions = summary.priority_actions || [];
  const upcoming = summary.upcoming_deliveries || [];

  const heroAction = priorityActions[0] || null;

  return (
    <div
      className="wyvio-signature-bg font-neue-haas -mx-6 lg:-mx-10 -my-8 lg:-my-12 min-h-screen"
      data-testid="page-dashboard-v2"
    >
      <main className="relative z-10 mx-auto max-w-[1600px] px-6 sm:px-8 lg:px-12 pb-24 lg:pb-32 pt-10 sm:pt-14 lg:pt-16">
        {/* Hero — CENTRE DE PILOTAGE + phrase intelligente */}
        <section className="mb-16 lg:mb-20 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
          <div className="min-w-0">
            <p className="mb-4 lg:mb-6 text-[10.5px] font-medium uppercase tracking-[0.3em] text-[#8A8A80]">
              Centre de pilotage · {fmtDate(summary.today)}
            </p>
            <h1 className="text-[44px] sm:text-[56px] lg:text-[72px] font-medium leading-[1] tracking-[-0.032em] text-[#151515]">
              Bonjour {firstName}.
            </h1>
            <p className="mt-6 lg:mt-8 max-w-[620px] text-[15px] lg:text-[17px] font-normal leading-[1.55] text-[#4A4A46]">
              {buildIntelligentSentence(t)}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <BtnSignature variant="ghost" icon={Sparkles} testid="cta-assistant">
              Assistant
            </BtnSignature>
            <BtnSignature
              variant="primary"
              arrow
              onClick={() => setShowForm(true)}
              testid="cta-new-dossier"
            >
              Nouveau dossier
            </BtnSignature>
          </div>
        </section>

        {/* Hero urgence — première priority action */}
        {heroAction && (
          <section className="mb-16 lg:mb-24" data-testid="section-urgent">
            <HeroUrgent
              testid="hero-urgent"
              onAction={() => navigate(`/app/dossier/${heroAction.id}`)}
              dossier={{
                vehicle: heroAction.vehicle_name || "Dossier prioritaire",
                client: `${heroAction.client_name || "Client"}${heroAction.commercial ? " · " + heroAction.commercial : ""}`,
                plate: heroAction.plate || "—",
                action: heroAction.action === "blocked" ? "Débloquer maintenant" : "Continuer",
                reason:
                  heroAction.action === "blocked"
                    ? `Ce dossier est bloqué sur l'étape « ${heroAction.step_label} ». Un clic permet d'ouvrir l'étape et de la débloquer.`
                    : `L'étape « ${heroAction.step_label} » attend une action de votre part pour continuer.`,
                dueLabel: heroAction.action === "blocked" ? "Blocage à lever" : "En attente d'action",
                location: heroAction.commercial ? `Commercial · ${heroAction.commercial}` : "Dossier prioritaire",
                image: null,
                silhouette: guessSilhouette(heroAction.vehicle_name),
                photosPending: false,
                completed: heroAction.completed_steps || 5,
              }}
            />
          </section>
        )}

        {/* 4 KPI */}
        <section
          className="mb-28 grid grid-cols-2 lg:grid-cols-4 gap-6"
          data-testid="kpi-row"
        >
          <KpiCard
            label="Dossiers actifs"
            value={t.active_dossiers || 0}
            isPrimary
            testid="kpi-active"
          />
          <KpiCard
            label="Livraisons aujourd'hui"
            value={t.todays_deliveries || 0}
            testid="kpi-deliveries"
          />
          <KpiCard
            label="Dossiers bloqués"
            value={t.blocked || 0}
            testid="kpi-blocked"
          />
          <KpiCard
            label="Documents attendus"
            value={t.documents_waiting || 0}
            testid="kpi-documents"
          />
        </section>

        {/* À traiter maintenant */}
        <section className="mb-28" data-testid="section-todo">
          <header className="mb-10 flex items-end justify-between">
            <div>
              <p className="mb-3 text-[10.5px] font-medium uppercase tracking-[0.28em] text-[#8A8A80]">
                Cockpit du jour
              </p>
              <h2 className="text-[40px] font-medium leading-none tracking-[-0.025em] text-[#151515]">
                À traiter maintenant
              </h2>
            </div>
            <button
              onClick={() => navigate("/app/dossiers")}
              className="group inline-flex items-center gap-1.5 text-[13px] font-medium text-[#151515]"
              data-testid="link-all-dossiers"
            >
              Voir tous les dossiers
              <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
          </header>

          {priorityActions.length === 0 ? (
            <EmptyToday />
          ) : (
            <div className="space-y-5">
              {priorityActions.slice(0, 5).map((a, i) => (
                <DossierCard
                  key={a.id + a.step_key}
                  dossier={{
                    vehicle: a.vehicle_name || "Dossier prioritaire",
                    plate: a.plate || "",
                    client: `${a.client_name || "Client"}${a.commercial ? " · " + a.commercial : ""}`,
                    status: a.action === "blocked" ? "bloque" : "encours",
                    action: a.action === "blocked" ? "Débloquer" : "Continuer",
                    urgent: a.action === "blocked",
                    image: null,
                    completed: a.completed_steps || 5,
                    silhouette: guessSilhouette(a.vehicle_name),
                    photosPending: !a.has_photos,
                  }}
                  onAction={() => navigate(`/app/dossier/${a.id}`)}
                  index={i}
                  testid={`dossier-${a.id}`}
                />
              ))}
            </div>
          )}
        </section>

        {/* Livraisons à venir + Rythme */}
        <section className="grid grid-cols-1 lg:grid-cols-3 gap-10" data-testid="section-upcoming">
          <div className="lg:col-span-2">
            <header className="mb-10 flex items-end justify-between">
              <div>
                <p className="mb-3 text-[10.5px] font-medium uppercase tracking-[0.28em] text-[#8A8A80]">
                  Planning du jour
                </p>
                <h2 className="text-[34px] font-medium leading-none tracking-[-0.025em] text-[#151515]">
                  Livraisons à venir
                </h2>
              </div>
              <BtnSignature
                variant="ghost"
                size="sm"
                icon={Plus}
                onClick={() => navigate("/app/planning")}
                testid="cta-planning"
              >
                Planning
              </BtnSignature>
            </header>

            {upcoming.length === 0 ? (
              <div className="shadow-wyvio-elev rounded-[24px] bg-white p-12 text-center">
                <p className="text-[14px] font-normal text-[#6B6B67]">
                  Aucune livraison prévue dans les 7 prochains jours.
                </p>
              </div>
            ) : (
              <div className="shadow-wyvio-elev rounded-[24px] bg-white p-3">
                {upcoming.slice(0, 6).map((d, i) => {
                  const dt = new Date(d.delivery_date || Date.now());
                  const time = dt.toLocaleTimeString("fr-FR", {
                    hour: "2-digit",
                    minute: "2-digit",
                  });
                  return (
                    <div key={d.id}>
                      <button
                        className="w-full text-left"
                        onClick={() => navigate(`/app/dossier/${d.id}`)}
                        data-testid={`delivery-${d.id}`}
                      >
                        <DeliveryCard
                          time={time.replace(":", "h")}
                          vehicle={d.vehicle_name || "Véhicule"}
                          client={d.client_name || "Client"}
                          address={d.commercial || "—"}
                          image={null}
                          silhouette={guessSilhouette(d.vehicle_name)}
                          isNext={i === 0}
                          index={i}
                          testid={`delivery-card-${d.id}`}
                        />
                      </button>
                      {i < Math.min(upcoming.length, 6) - 1 && (
                        <div className="mx-4 h-px bg-[#F0F0EB]" />
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Panneau Rythme */}
          <aside className="lg:col-span-1">
            <p className="mb-8 mt-1 text-[10.5px] font-medium uppercase tracking-[0.3em] text-[#8A8A80]">
              Rythme de l&apos;agence
            </p>

            <div className="shadow-wyvio-elev rounded-[24px] bg-white p-10">
              <p className="text-[10.5px] font-medium uppercase tracking-[0.2em] text-[#8A8A80]">
                En temps réel
              </p>
              <div className="mt-6 flex items-baseline gap-2">
                <span className="wyvio-tabular text-[68px] font-medium leading-[0.9] tracking-[-0.035em] text-[#151515]">
                  {t.ready_for_delivery || 0}
                </span>
                <span className="text-[15px] font-normal text-[#8A8A80]">prêtes</span>
              </div>
              <p className="mt-5 text-[13.5px] font-normal leading-[1.55] text-[#4A4A46]">
                Livraisons prêtes à partir dès que le client confirme.
              </p>

              <div className="my-8 h-px w-full bg-[#E8E8E2]" />

              <div className="space-y-5">
                {[
                  { label: "Paiements en attente", value: t.waiting_payments || 0 },
                  { label: "Documents attendus", value: t.documents_waiting || 0 },
                  { label: "Messages non lus", value: t.unread_messages || 0 },
                ].map((r, i) => (
                  <div key={i} className="flex items-baseline justify-between">
                    <span className="text-[12.5px] font-normal text-[#6B6B67]">
                      {r.label}
                    </span>
                    <span className="wyvio-tabular text-[15px] font-medium tracking-[-0.01em] text-[#151515]">
                      {r.value}
                    </span>
                  </div>
                ))}
              </div>

              <button
                onClick={() => navigate("/app/dossiers")}
                className="group mt-10 inline-flex items-center gap-1.5 text-[13px] font-medium text-[#151515]"
                data-testid="cta-detail-rythme"
              >
                Voir tous les dossiers
                <ChevronRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
              </button>
            </div>
          </aside>
        </section>
      </main>

      {showForm && (
        <DossierForm
          mode="create"
          onClose={() => setShowForm(false)}
          onSaved={(created) => {
            if (created?.id) navigate(`/app/dossier/${created.id}`);
            else window.location.reload();
          }}
        />
      )}
    </div>
  );
}

function EmptyToday() {
  return (
    <div className="shadow-wyvio-elev rounded-[24px] bg-white p-16 text-center">
      <div className="mx-auto mb-5 flex h-12 w-12 items-center justify-center rounded-full bg-[#F2F2EE]">
        <Sparkles className="h-5 w-5 text-[#151515]" strokeWidth={1.5} />
      </div>
      <p className="text-[16px] font-medium tracking-[-0.01em] text-[#151515]">
        Tout est à jour.
      </p>
      <p className="mt-2 text-[13px] font-normal text-[#6B6B67]">
        Aucune action prioritaire pour l&apos;instant.
      </p>
    </div>
  );
}
